import { resolveAbsoluteUrl } from "./resolve-absolute-url"

export type BodyType<BodyData> = BodyData
export type ErrorType<Error> = Error

export type ApiClientOptions = {
	/** Resolved lazily on each request — the base URL may depend on the current hostname. */
	getBaseUrl: () => string
	/**
	 * Refresh the session after a 401. Return false when the session cannot be
	 * renewed; the original 401 is then returned to the caller. Any other
	 * result retries that request once. Concurrent 401s share one refresh.
	 */
	onUnauthorized?: () => Promise<boolean>
}

const getBody = async <T>(response: Response | Request): Promise<T> => {
	const contentType = response.headers.get("content-type") ?? ""

	if (contentType.includes("application/json")) {
		return response.json() as Promise<T>
	}

	if (
		contentType.includes("application/pdf") ||
		contentType.includes("application/octet-stream") ||
		contentType.includes(
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		) ||
		contentType.includes("application/zip")
	) {
		return response.blob() as Promise<T>
	}

	return response.text() as Promise<T>
}

const getUrl = (contextUrl: string, getBaseUrl: () => string): string => {
	const baseUrl = resolveAbsoluteUrl(getBaseUrl())
	const base = new URL(baseUrl)
	const basePath = base.pathname.endsWith("/")
		? base.pathname.slice(0, -1)
		: base.pathname
	const [contextPath, contextQuery] = contextUrl.split("?")
	const path = contextPath.startsWith("/") ? contextPath : `/${contextPath}`
	base.pathname = `${basePath}${path}`
	base.search = contextQuery ?? ""

	return base.href
}

const getHeaders = (
	headers?: HeadersInit,
	body?: BodyInit | null,
): HeadersInit => {
	const result = new Headers(headers)

	const isFormData = typeof FormData !== "undefined" && body instanceof FormData
	const isBlob = typeof Blob !== "undefined" && body instanceof Blob
	const hasBody = body != null
	if (hasBody && !isFormData && !isBlob && !result.has("Content-Type")) {
		result.set("Content-Type", "application/json")
	}

	return result
}

/**
 * Creates a fetch-based HTTP client (Orval mutator shape) that:
 * - sends the session cookie with every request (`credentials: "include"`)
 * - resolves its base URL lazily via `getBaseUrl` (hostname-dependent config)
 * - on 401, refreshes the session once (single-flight) and retries that request once
 */
export function createApiClient({
	getBaseUrl,
	onUnauthorized,
}: ApiClientOptions) {
	let refreshInFlight: Promise<boolean> | null = null

	const refreshSession = (): Promise<boolean> => {
		if (!onUnauthorized) {
			return Promise.resolve(false)
		}
		if (!refreshInFlight) {
			refreshInFlight = Promise.resolve()
				.then(() => onUnauthorized())
				.then((renewed) => renewed)
				.catch(() => false)
				.finally(() => {
					refreshInFlight = null
				})
		}
		return refreshInFlight
	}

	const customFetch = async <T>(
		url: string,
		options: RequestInit,
		attempt = 0,
	): Promise<T> => {
		try {
			const requestUrl = getUrl(url, getBaseUrl)
			const headers = getHeaders(options.headers, options.body ?? null)

			const requestInit: RequestInit = {
				...options,
				headers,
				credentials: "include",
			}

			const request = new Request(requestUrl, requestInit)
			const response = await fetch(request)

			if (
				response.status === 401 &&
				attempt === 0 &&
				onUnauthorized &&
				!options.signal?.aborted
			) {
				const renewed = await refreshSession()
				if (renewed && !options.signal?.aborted) {
					return customFetch<T>(url, options, attempt + 1)
				}
			}

			if (!response.ok) {
				let data: unknown
				try {
					data = await getBody(response)
				} catch {
					data = null
				}
				const errorData = {
					data,
					status: response.status,
					message: response.statusText,
				}
				throw errorData
			}

			const data = await getBody<T>(response)

			return {
				status: response.status,
				data,
				headers: response.headers,
			} as T
		} catch (error: unknown) {
			const isAbortError =
				typeof error === "object" &&
				error !== null &&
				"name" in error &&
				error.name === "AbortError"
			if (!isAbortError) {
				console.error("Error fetching data:", error)
			}
			throw error
		}
	}

	return customFetch
}
